import { useMemo } from 'react'
import { ENDPOINT_FILTERS, PROPERTY_VALUE_ENDPOINTS, propertyTransactionsFieldsConfig } from '../../api/properties/formValuesConfig'
import { Property, PropertyCreatePayload } from '../../api/properties/types'
import { PROPERTY_STATUS } from '../../common/constants'
import { AgentPrintData } from '../../Components/Modals/AgentPrintModal/types'

export const getFilteredProperty = (property: Property) => ({
    id: property.id,
    internalAppId: property.internalAppId,
    area: property.area,
    price: property.price,
    priceOnRequest: property.priceOnRequest,
    priceM2: property.priceM2,
    floorNo: property.floorNo,
    bedrooms: property.bedroomsNo,
    translations: property.translations,
    propertyService: property.propertyService,
    propertyType: property.propertyType,
    propertySubType: property.propertySubType,
    propertyStatus: property.propertyStatus,
    city: property.city,
    widerArea: property.widerArea,
    primaryAgent: {
        id: property.primaryAgent?.id,
        firstName: property.primaryAgent?.firstName,
        lastName: property.primaryAgent?.lastName,
        email: property.primaryAgent?.primaryEmail,
        mobileNumber: property.primaryAgent?.mobileNumber,
        agencyOffice: property.primaryAgent?.agencyOffice,
    },
})

export const getPropertyQuickViewData = (property: Property) => {
    return {
        id: property.id,
        internalAppId: property.internalAppId,
        propertySubType: property.propertySubType,
        propertyService: property.propertyService,
        city: property.city,
        widerArea: property.widerArea,
        quarter: property.quarter,
        street: property.street,
        streetNumber: property.streetNumber,
        country: property.country,
        price: property.price,
        area: property.area,
        propertyRoomsNo: property.propertyRoomsNo,
        propertyFloorPosition: property.propertyFloorPosition,
        floorNo: property.floorNo,
        floorsTotalNo: property.floorsTotalNo,
        mainPhoto: property.mainPhoto
            ? {
                  id: property.mainPhoto.id,
                  updated: property.mainPhoto.updated,
              }
            : undefined,
        propertyCondition: property.propertyCondition,
        propertyDescriptions: property.propertyDescriptions,
        propertyTransaction: property.propertyTransaction
            ? {
                  propertyLandRegistryStatus: property.propertyTransaction.propertyLandRegistryStatus,
              }
            : undefined,
        translations: property.translations,
    }
}

export const getAgentPrintData = (property: Property): AgentPrintData => ({
    id: property.id,
    internalAppId: property.internalAppId,
    propertyReceiptDate: property.propertyReceiptDate,
    propertyStatus: property.propertyStatus,
    propertyService: property.propertyService,
    propertyType: property.propertyType,
    propertySubType: property.propertySubType,
    country: property.country,
    county: property.county,
    city: property.city,
    widerArea: property.widerArea,
    street: property.street,
    streetNumber: property.streetNumber,
    price: property.price,
    priceM2: property.priceM2,
    area: property.area,
    propertyOrientation: property.propertyOrientation,
    propertyRoomsNo: property.propertyRoomsNo,
    constructionYear: property.constructionYear,
    adaptationYear: property.adaptationYear,
    floorNo: property.floorNo,
    floorsTotalNo: property.floorsTotalNo,
    propertyHeating: property.propertyHeating,
    propertyWindowsDoors: property.propertyWindowsDoors,
    agencyCommission: property.agencyCommission,
    translations: property.translations,
    agent: {
        id: property.primaryAgent?.id,
        firstName: property.primaryAgent?.firstName,
        lastName: property.primaryAgent?.lastName,
        phoneNumber: property.primaryAgent?.phoneNumber,
        mobileNumber: property.primaryAgent?.mobileNumber,
        primaryEmail: property.primaryAgent?.primaryEmail,
    },
    owner: (property.owner ?? []).map((o) => ({
        id: o.id,
        contactType: o.contactType,
        contactPerson: o.contactPerson,
        contactCompany: o.contactCompany,
        phone: o.contactPhone?.[0]?.phoneNumber ?? null,
        email: o.contactEmail?.[0]?.email ?? null,
    })),
    propertyTransaction: property.propertyTransaction
        ? {
              propertyRegistryInsert: property.propertyTransaction.propertyRegistryInsert,
              propertyCadastralMunicipality: property.propertyTransaction.propertyCadastralMunicipality,
          }
        : null,
})

export const useFormDataExtraction = (generalValues: PropertyCreatePayload) => {
    const {
        country_id,
        county_id,
        city_id,
        internal_id,
        property_receipt_date,
        contract_no,
        contract_date,
        owner_id,
        property_supply_source_id,
        project_id,
    } = generalValues

    const locationGeneralValues = useMemo(() => ({ country_id, county_id, city_id }), [country_id, county_id, city_id])

    const sideFormValues = useMemo(
        () => ({ internal_id, property_receipt_date, contract_no, contract_date, owner_id, property_supply_source_id, project_id }),
        [internal_id, property_receipt_date, contract_no, contract_date, owner_id, property_supply_source_id, project_id]
    )

    return { locationGeneralValues, sideFormValues }
}

export const getValidData = <T>(data: Record<string, T>) => {
    return Object.entries(data).reduce<Record<string, T>>((acc, curr) => {
        const [key, value] = curr

        if (isFilledValue(value)) {
            acc[key] = value
        }
        return acc
    }, {})
}

const isFilledValue = (value: unknown): boolean => {
    if (Array.isArray(value)) {
        return value.length > 0 && value.every((obj) => obj.owner_id !== '')
    }
    return value !== '' && value !== null && value !== undefined
}

export const handlePropertyStatusColor = (statusId: number) => {
    if (!statusId) return ''

    const colorConfig = {
        [PROPERTY_STATUS.RESERVED]: '#007ca4',
        [PROPERTY_STATUS.SOLD]: '#ff5200',
        [PROPERTY_STATUS.RENTED]: '#2f8926',
        [PROPERTY_STATUS.SOLD_OWNER_ARCHIVED]: '#fca9c1',
        [PROPERTY_STATUS.RENTED_OWNER_ARCHIVED]: '#a9ebff',
        [PROPERTY_STATUS.WITHDRAWN_OFFER_ARCHIVED]: '#e1cecd',
        [PROPERTY_STATUS.SOLD_ARCHIVED]: '#fdc2aa',
        [PROPERTY_STATUS.RENTED_ARCHIVED]: '#d1e1c9',
    }

    return colorConfig[statusId]
}

/**
 * Splits property value endpoints into general (static/dynamic), and transaction (static/dynamic) fields
 * @param excludeFields - Array of field names to exclude from the result (e.g., market-specific fields)
 */
export const splitPropertyValueFields = (excludeFields: string[] = []) => {
    const allFields = Object.keys(PROPERTY_VALUE_ENDPOINTS)
    const dynamicFieldKeys = Object.keys(ENDPOINT_FILTERS)
    const transactionFieldKeys = Object.keys(propertyTransactionsFieldsConfig)

    return allFields.reduce(
        (acc, field) => {
            if (excludeFields.includes(field)) {
                return acc
            }

            const isDynamic = dynamicFieldKeys.includes(field)
            const isTransaction = transactionFieldKeys.includes(field)

            switch (true) {
                case isTransaction && isDynamic:
                    acc.transactionDynamicFields.push(field)
                    break
                case isTransaction:
                    acc.transactionStaticFields.push(field)
                    break
                case isDynamic:
                    acc.generalDynamicFields.push(field)
                    break
                default:
                    acc.generalStaticFields.push(field)
            }

            return acc
        },
        {
            generalStaticFields: [] as string[],
            generalDynamicFields: [] as string[],
            transactionStaticFields: [] as string[],
            transactionDynamicFields: [] as string[],
        }
    )
}
